import {
  AttributeFormat,
  getFetcher,
  patchFetcher,
  putFetcher,
} from "shared/api/api";
import client from "shared/api/axios";
import { createURL } from "shared/api/utils";
import { MileageUnit } from "shared/types";

import { PagesConfig } from "pages/SuperAdmin/PagesConfig/types";

const SUPER_ADMIN_BASE_ROUTE = "superAdmin";

/**
 * Attributes config
 */
const ATTRIBUTES_CONFIG_BASE = "attributesConfig";
const ATTRIBUTE_CONFIG_READ_ONLY_FIELDS: (keyof AttributeConfig)[] = [
  "required",
  "unitType",
  "unit",
  "configurable",
];

export const stripReadOnlyFields = (
  config: AttributeConfig[]
): AttributeConfig[] =>
  config.map((item) => {
    const clone = { ...item };
    ATTRIBUTE_CONFIG_READ_ONLY_FIELDS.forEach((key) => delete clone[key]);

    return clone;
  });

// Which of an attribute's capability toggles the API lets a super admin change.
// A flag reported false is pinned to the value the config already carries — the
// attribute's data type, kind or schema means the runtime either ignores that
// toggle or forces it — so the UI renders the switch read-only.
export interface AttributeConfigurableFlags {
  sorting: boolean;
  filtering: boolean;
  values: boolean;
  groupBy: boolean;
  export: boolean;
  hideInTable: boolean;
  hideFilter: boolean;
  globalSearch: boolean;
  textSearchable: boolean;
  byVehicleAgeBirthday: boolean;
  byVehicleAgeExposure: boolean;
}

export interface AttributeConfig {
  ID: string;
  displayName: string;
  description: string | null;
  sorting: boolean;
  filtering: boolean;
  values: boolean;
  groupBy: boolean;
  export: boolean;
  hideInTable: boolean;
  hideFilter: boolean;
  byVehicleAgeBirthday: boolean;
  byVehicleAgeExposure: boolean;
  byVehicleAgeExposureBuckets: number[];
  unitAtRest: string | null;
  unitType: string | null;
  unit: string | null;
  required: boolean;
  columnWidth: number | null;
  enabled: boolean;
  globalSearch: boolean;
  textSearchable: boolean;
  // Optional because an API that predates this field simply omits it; readers must
  // fall back to treating every toggle as editable rather than assume it is there.
  configurable?: AttributeConfigurableFlags;
  // The effective format, merged over the data type's defaults by the API; null when the data type has no format.
  // Absent from an API that predates it. Sending null resets the attribute to the defaults.
  format?: AttributeFormat | null;
}

interface AdminAttributesConfigRequest {
  resource: string;
}

interface AdminAttributesConfigUpdateRequest extends AdminAttributesConfigRequest {
  config: object;
}

export interface AdminAttributeConfig {
  resource: string;
  endpoint: string;
}

export const listAdminAttributesConfigRequestURI = (): string =>
  client.getUri({
    method: "GET",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, ATTRIBUTES_CONFIG_BASE]),
  });

const getAdminAttributesConfigRequestURI = ({
  resource,
  ...params
}: AdminAttributesConfigRequest): string =>
  client.getUri({
    method: "GET",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, ATTRIBUTES_CONFIG_BASE, resource]),
    params,
  });

export const getAdminAttributesConfig = (args: AdminAttributesConfigRequest) =>
  getFetcher<AttributeConfig[]>(getAdminAttributesConfigRequestURI(args));

const updateAdminAttributesConfigRequestURI = ({
  resource,
}: AdminAttributesConfigRequest): string =>
  client.getUri({
    method: "put",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, ATTRIBUTES_CONFIG_BASE, resource]),
  });

export const updateAdminAttributesConfig = ({
  resource,
  ...args
}: AdminAttributesConfigUpdateRequest) =>
  putFetcher<AttributeConfig[]>(
    updateAdminAttributesConfigRequestURI({ resource }),
    { data: stripReadOnlyFields(args.config as AttributeConfig[]) }
  );

/**
 * Pages config
 */
const PAGES_CONFIG_BASE = "pagesConfig";

export interface AdminPagesConfigRequest {}

interface AdminPagesConfigUpdateRequest {
  config: PagesConfig;
}

interface AdminPagesConfigUpdateResponse {
  // Resources whose attributes the API enabled with defaults because a page
  // using them got enabled.
  enabledResources: string[];
}

export const getAdminPagesConfigRequestURI = (
  params: AdminPagesConfigRequest
): string =>
  client.getUri({
    method: "GET",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, PAGES_CONFIG_BASE]),
    params,
  });

const updateAdminPagesConfigRequestURI = (): string =>
  client.getUri({
    method: "PUT",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, PAGES_CONFIG_BASE]),
  });

export const updateAdminPagesConfig = (args: AdminPagesConfigUpdateRequest) =>
  putFetcher<AdminPagesConfigUpdateResponse>(
    updateAdminPagesConfigRequestURI(),
    {
      data: args.config,
    }
  );

/**
 * General config
 */
const GENERAL_CONFIG_BASE = "generalConfig";

export interface MetabaseDashboard {
  id: number;
  name: string;
}

export type TopHeaderConfigType = {
  title?: string;
  searchTextPlaceholder?: string;
  tenantIconSvg?: string;
  tenantIconImage?: string;
};

export interface GeneralConfigModel {
  mileageUnit: MileageUnit;
  helpContactEmail: string | null;
  rootPage: string | null;
  rootPageMobile: string | null;
  metabaseDashboards: MetabaseDashboard[];
  signalEventsTimelineResponseLimit: number | null;
  timezone: string;
  topHeader: TopHeaderConfigType | null;
  distanceUnit: string;
  temperatureUnit: string;
  pressureUnit: string;
  voltageUnit: string;
  percentageUnit: string;
  frequencyUnit: string;
  timeUnit: string;
  currentUnit: string;
  energyUnit: string;
  angleUnit: string;
  speedUnit: string;
  currencyUnit: string;
  language: string;
  languageCode: string;
  dateTimeFormat: string | null;
}

export interface GeneralConfigRequest {}

interface GeneralConfigUpdateRequest {
  config: GeneralConfigModel;
}

export const getGeneralConfigRequestURI = (
  params: GeneralConfigRequest
): string =>
  client.getUri({
    method: "GET",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, GENERAL_CONFIG_BASE]),
    params,
  });

const updateGeneralConfigRequestURI = (): string =>
  client.getUri({
    method: "PUT",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, GENERAL_CONFIG_BASE]),
  });

export const updateGeneralConfig = (args: GeneralConfigUpdateRequest) =>
  patchFetcher<GeneralConfigModel>(
    updateGeneralConfigRequestURI(),
    args.config
  );

/**
 * Vehicle Images
 */
export interface VehicleImage {
  imageData: string; // Base64 encoded image data
  contentType: string;
  imageOrder: number;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleModelYear?: number | null;
  trimLevel?: string | null;
}

export const listAdminVehicleImagesRequestURI = (): string =>
  client.getUri({
    method: "GET",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, "vehicleImages"]),
  });

const updateAdminVehicleImagesRequestURI = (): string =>
  client.getUri({
    method: "PUT",
    url: createURL([SUPER_ADMIN_BASE_ROUTE, "vehicleImages"]),
  });

export const updateAdminVehicleImages = (images: VehicleImage[]) =>
  putFetcher<VehicleImage[]>(updateAdminVehicleImagesRequestURI(), {
    data: images,
  });
